import React from 'react';
import { useLocalSearchParams } from 'expo-router';

import { useIsFocused } from '@sg/garnish';

import {
  MenuContent,
  useMenuContent,
  useMenuContentData,
  useProductDetailsContentData,
} from '../../components';
import { useMenuReorderRailOrders } from '../../hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const MenuScreen = () => {
  //
  // ─── Navigation ──────────────────────────────────────────────────────

  const { restaurantSlug } = useLocalSearchParams<{ restaurantSlug: string }>();

  // ─── Remote Data ─────────────────────────────────────────────────────

  const { restaurantData, isFetching, hasError, hasNoProducts } = useMenuContentData({
    restaurantSlug,
  });
  const { productDetailsContentData } = useProductDetailsContentData();
  const { mappedContent, isFetchingMenuContent } = useMenuContent();

  const { categoriesBadges, caloriesOverrides, denseCategoryIds } = mappedContent;

  const numericRestaurantId = restaurantData ? Number.parseInt(restaurantData.id, 10) : 0;

  const reorderRailOrders = useMenuReorderRailOrders({
    restaurantId: Number.isNaN(numericRestaurantId) ? 0 : numericRestaurantId,
    enabled: Boolean(restaurantData),
  });

  // ─────────────────────────────────────────────────────────────────────

  const isScreenFocused = useIsFocused();
  const shouldRenderErrorView = hasError || !restaurantData || hasNoProducts;

  // When the menu screen is rendered without being focused, there won't be any data available.
  const isWaitingForFocus = !isScreenFocused && shouldRenderErrorView;

  if (
    isFetching ||
    isFetchingMenuContent ||
    isWaitingForFocus ||
    reorderRailOrders.isFetchingMostRecentOrders
  ) {
    return <MenuContent.Loading />;
  }

  if (shouldRenderErrorView) {
    return <MenuContent.Error />;
  }

  // ─────────────────────────────────────────────────────────────────────

  const orderChannel = restaurantData.isOutpost ? 'outpost' : 'pickup';
  const locationName = restaurantData.name;

  return (
    <MenuContent.Ready
      // NOTE: We use the restaurant ID as the key to force a re-render when
      //       the restaurant data changes to prevent having a stale data and components.
      key={restaurantData.id}
      restaurantData={restaurantData}
      productDetailsContentData={productDetailsContentData}
      categoriesBadges={categoriesBadges}
      caloriesOverrides={caloriesOverrides}
      denseCategoryIds={denseCategoryIds}
      orderChannel={orderChannel}
      locationName={locationName}
      reorderRailOrders={reorderRailOrders}
    />
  );
};
